"use client";

import { useState, useEffect, useCallback, useRef } from "react";

const GRID_SIZE = 10;
const GAME_TIME = 60;

type Cell = number | null;
type Grid = Cell[][];
type Pos = [number, number];

interface ScoreRecord {
  name: string;
  score: number;
  created_at: string;
}

const NUM_COLORS: Record<number, string> = {
  1: "text-red-500",
  2: "text-orange-500",
  3: "text-amber-500",
  4: "text-green-500",
  5: "text-teal-500",
  6: "text-blue-500",
  7: "text-indigo-500",
  8: "text-purple-500",
  9: "text-pink-500",
};

const NUM_BG: Record<number, string> = {
  1: "bg-red-50",
  2: "bg-orange-50",
  3: "bg-amber-50",
  4: "bg-green-50",
  5: "bg-teal-50",
  6: "bg-blue-50",
  7: "bg-indigo-50",
  8: "bg-purple-50",
  9: "bg-pink-50",
};

function makeGrid(): Grid {
  return Array.from({ length: GRID_SIZE }, () =>
    Array.from({ length: GRID_SIZE }, () => Math.floor(Math.random() * 9) + 1)
  );
}

function isAdjacent(r1: number, c1: number, r2: number, c2: number) {
  return (
    Math.abs(r1 - r2) <= 1 &&
    Math.abs(c1 - c2) <= 1 &&
    !(r1 === r2 && c1 === c2)
  );
}

function applyGravity(grid: Grid, removed: Set<string>): Grid {
  const newGrid: Grid = Array.from({ length: GRID_SIZE }, () =>
    Array(GRID_SIZE).fill(null)
  );
  for (let c = 0; c < GRID_SIZE; c++) {
    const col: number[] = [];
    for (let r = GRID_SIZE - 1; r >= 0; r--) {
      const key = `${r},${c}`;
      if (!removed.has(key) && grid[r][c] !== null) {
        col.push(grid[r][c] as number);
      }
    }
    while (col.length < GRID_SIZE) {
      col.push(Math.floor(Math.random() * 9) + 1);
    }
    for (let r = GRID_SIZE - 1; r >= 0; r--) {
      newGrid[r][c] = col[GRID_SIZE - 1 - r];
    }
  }
  return newGrid;
}

export default function Game() {
  const [gameState, setGameState] = useState<
    "name-entry" | "playing" | "finished"
  >("name-entry");
  const [playerName, setPlayerName] = useState("");
  const [grid, setGrid] = useState<Grid>(makeGrid);
  const [selected, setSelected] = useState<Pos | null>(null);
  const [timeLeft, setTimeLeft] = useState(GAME_TIME);
  const [score, setScore] = useState(0);
  const [animating, setAnimating] = useState<Set<string>>(new Set());
  const [leaderboard, setLeaderboard] = useState<ScoreRecord[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [myRank, setMyRank] = useState<number | null>(null);
  const submitted = useRef(false);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const res = await fetch("/api/scores");
      const data = await res.json();
      setLeaderboard(data);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  // Timer
  useEffect(() => {
    if (gameState !== "playing") return;
    const interval = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          setGameState("finished");
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [gameState]);

  // Submit score when game finishes
  useEffect(() => {
    if (gameState !== "finished" || submitted.current) return;
    submitted.current = true;
    const doSubmit = async () => {
      setSubmitting(true);
      try {
        await fetch("/api/scores", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name: playerName, score }),
        });
        const res = await fetch("/api/scores");
        const data: ScoreRecord[] = await res.json();
        setLeaderboard(data);
        const rank = data.findIndex(
          (d) => d.name === playerName && d.score === score
        );
        setMyRank(rank >= 0 ? rank + 1 : null);
      } catch {
        // ignore
      }
      setSubmitting(false);
    };
    doSubmit();
  }, [gameState, playerName, score]);

  const handleCellClick = useCallback(
    (r: number, c: number) => {
      if (gameState !== "playing") return;
      if (animating.has(`${r},${c}`)) return;

      if (!selected) {
        setSelected([r, c]);
        return;
      }
      const [sr, sc] = selected;
      if (sr === r && sc === c) {
        setSelected(null);
        return;
      }

      if (isAdjacent(sr, sc, r, c)) {
        const a = grid[sr][sc];
        const b = grid[r][c];
        if (a !== null && b !== null && a + b === 10) {
          // Match!
          const removed = new Set([`${sr},${sc}`, `${r},${c}`]);
          setAnimating(removed);
          setSelected(null);
          setTimeout(() => {
            setGrid((g) => applyGravity(g, removed));
            setAnimating(new Set());
            setScore((s) => s + 1);
          }, 400);
          return;
        }
      }
      setSelected([r, c]);
    },
    [gameState, selected, animating, grid]
  );

  const startGame = () => {
    if (!playerName.trim()) return;
    setGrid(makeGrid());
    setScore(0);
    setTimeLeft(GAME_TIME);
    setSelected(null);
    setAnimating(new Set());
    setMyRank(null);
    submitted.current = false;
    setGameState("playing");
  };

  const resetGame = () => {
    fetchLeaderboard();
    setGameState("name-entry");
  };

  // ── NAME ENTRY ──
  if (gameState === "name-entry") {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 flex flex-col items-center justify-start py-8 px-4">
        <div className="w-full max-w-md">
          <h1 className="text-4xl font-black text-center mb-1 text-purple-700">
            🎮 合計10ゲーム
          </h1>
          <p className="text-center text-purple-400 text-sm mb-6 font-bold">
            隣の数字を選んで合計10を作ろう！
          </p>

          <div className="bg-white rounded-3xl shadow-xl p-6 mb-6">
            <p className="text-gray-500 text-sm mb-4 text-center">
              🕐 {GAME_TIME}秒で何ペア消せるかな？
            </p>
            <input
              type="text"
              placeholder="なまえを入れてにゃん 🐾"
              value={playerName}
              onChange={(e) => setPlayerName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && startGame()}
              maxLength={20}
              className="w-full border-2 border-purple-200 rounded-2xl px-4 py-3 text-center text-lg font-bold focus:outline-none focus:border-purple-400 mb-4"
            />
            <button
              onClick={startGame}
              disabled={!playerName.trim()}
              className="w-full bg-gradient-to-r from-pink-400 to-purple-500 text-white font-black text-xl py-4 rounded-2xl shadow-lg disabled:opacity-40 active:scale-95 transition-transform"
            >
              スタート！🚀
            </button>
          </div>

          {/* How to play */}
          <div className="bg-white/70 rounded-2xl p-4 mb-6 text-sm text-gray-600">
            <p className="font-bold text-purple-600 mb-2">🎯 あそびかた</p>
            <p>・タテ・ヨコ・ナナメに隣の数字を2つ選ぶ</p>
            <p>・合計が10になったら消える！</p>
            <p>・消えた後は上から新しい数字が落ちてくる</p>
          </div>

          {/* Leaderboard */}
          <Leaderboard records={leaderboard} highlight={null} />
        </div>
      </div>
    );
  }

  // ── PLAYING ──
  if (gameState === "playing") {
    const pct = (timeLeft / GAME_TIME) * 100;
    const timerColor =
      timeLeft <= 10
        ? "bg-red-400"
        : timeLeft <= 20
        ? "bg-orange-400"
        : "bg-green-400";

    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 flex flex-col items-center py-4 px-2">
        {/* Header */}
        <div className="w-full max-w-md mb-3 px-2">
          <div className="flex justify-between items-center mb-2">
            <span className="text-2xl font-black text-purple-700">
              🎯 {score} ペア
            </span>
            <span
              className={`text-2xl font-black ${
                timeLeft <= 10 ? "text-red-500 animate-pulse" : "text-gray-700"
              }`}
            >
              ⏱ {timeLeft}
            </span>
          </div>
          {/* Timer bar */}
          <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all duration-1000 ${timerColor}`}
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Grid */}
        <div className="bg-white rounded-3xl shadow-xl p-2">
          <div
            className="grid gap-1"
            style={{ gridTemplateColumns: `repeat(${GRID_SIZE}, 1fr)` }}
          >
            {grid.map((row, r) =>
              row.map((cell, c) => {
                const key = `${r},${c}`;
                const isSelected =
                  selected && selected[0] === r && selected[1] === c;
                const isAnim = animating.has(key);
                return (
                  <button
                    key={key}
                    onClick={() => handleCellClick(r, c)}
                    className={[
                      "w-8 h-8 rounded-lg font-black text-sm flex items-center justify-center transition-all duration-300 select-none",
                      cell !== null ? NUM_BG[cell] : "bg-gray-100",
                      cell !== null ? NUM_COLORS[cell] : "text-gray-300",
                      isSelected
                        ? "ring-2 ring-pink-500 scale-110 shadow-lg bg-pink-100"
                        : "",
                      isAnim
                        ? "scale-150 opacity-0 bg-yellow-200"
                        : "hover:scale-105 active:scale-95",
                    ].join(" ")}
                  >
                    {cell}
                  </button>
                );
              })
            )}
          </div>
        </div>

        <p className="text-xs text-purple-300 mt-3 font-bold">
          タテ・ヨコ・ナナメの隣を選んで合計10！
        </p>
      </div>
    );
  }

  // ── FINISHED ──
  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-50 to-blue-100 flex flex-col items-center py-8 px-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-3xl shadow-xl p-6 mb-6 text-center">
          <p className="text-4xl mb-2">⏰</p>
          <h2 className="text-2xl font-black text-purple-700 mb-1">
            タイムアップ！
          </h2>
          <p className="text-gray-500 mb-4">{playerName} さんのスコア</p>
          <p className="text-6xl font-black text-pink-500 mb-1">{score}</p>
          <p className="text-gray-400 text-lg mb-4">ペア</p>
          {myRank && (
            <p className="text-purple-600 font-bold text-lg mb-4">
              {myRank === 1 ? "👑" : myRank === 2 ? "🥈" : myRank === 3 ? "🥉" : "🎉"}{" "}
              {myRank}位！
            </p>
          )}
          {submitting && (
            <p className="text-gray-400 text-sm animate-pulse mb-4">
              記録を保存中…
            </p>
          )}
          <button
            onClick={resetGame}
            className="w-full bg-gradient-to-r from-pink-400 to-purple-500 text-white font-black text-xl py-4 rounded-2xl shadow-lg active:scale-95 transition-transform"
          >
            もう一度！🔄
          </button>
        </div>

        <Leaderboard records={leaderboard} highlight={{ name: playerName, score }} />
      </div>
    </div>
  );
}

function Leaderboard({
  records,
  highlight,
}: {
  records: ScoreRecord[];
  highlight: { name: string; score: number } | null;
}) {
  if (records.length === 0) return null;
  const top3 = records.slice(0, 3);
  const medals = ["👑", "🥈", "🥉"];
  const bgColors = ["bg-yellow-50 ring-2 ring-yellow-300", "bg-gray-50 ring-2 ring-gray-300", "bg-orange-50 ring-2 ring-orange-200"];
  return (
    <div className="bg-white rounded-3xl shadow-xl p-4">
      <h3 className="text-lg font-black text-purple-700 mb-4 text-center">
        🏆 ランキング TOP 3
      </h3>
      <div className="flex flex-col gap-3">
        {top3.map((r, i) => {
          const isMe =
            highlight && r.name === highlight.name && r.score === highlight.score;
          return (
            <div
              key={i}
              className={`flex items-center gap-3 px-4 py-3 rounded-2xl ${
                isMe ? "bg-pink-100 ring-2 ring-pink-400" : bgColors[i]
              }`}
            >
              <span className="text-3xl">{medals[i]}</span>
              <span className="flex-1 font-black text-gray-700 text-base truncate">
                {r.name}
              </span>
              <span className="font-black text-purple-600 text-lg">
                {r.score}<span className="text-sm font-normal text-gray-400"> ペア</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
